import { useMemo, useRef, useState } from 'react';
import { required, useNotify } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useRunRequest } from '@/hooks';
import _ from 'lodash';
import { uploadToS3 } from '@/services/aws';
import { ValidDocTypes } from '@/services/aws/uploadToS3/enums';
import { UploadToS3Response } from '@/services/aws/uploadToS3/types';
import { setIsLoading } from '@/store/slices/turnAdmin';
import { OneOffCheckRecord } from '@/types/turn-api';
import { TurnActionsEnum, TurnResourcesEnum, TurnTargetsEnum } from '@/types/turn-enums';
import { sanitizeFileName } from './adapters';
import { OVERRIDE_ERROR_MESSAGE, path, REQUIRES_PROOF, STATUS_CHOICES } from './constants';
import { FormFields, ReportValidationState } from './types';
import { RequestOneOffCheckProps } from '../types';
import {
  IDLE_VALIDATION,
  requiresReportValidation,
  saveBlockedByValidation,
  toValidationState,
  validationRetryText,
  validationSuccessLines,
} from './utils';

const useController = ({ checks, onClose, record, worker }: RequestOneOffCheckProps) => {
  const notify = useNotify();
  const dispatch = useDispatch();
  const { handleSubmit, setValue, watch } = useFormContext<FormFields>();
  const { runRequest } = useRunRequest();
  const [fileUrl, setFileUrl] = useState<string>(null);
  const [pendingFile, setPendingFile] = useState<{ name: string; size: number } | null>(null);
  const [uploading, setUploading] = useState(false);
  const uploadLock = useRef(false);
  const [reportCleared, setReportCleared] = useState(false);
  const [validation, setValidation] = useState<ReportValidationState>(IDLE_VALIDATION);
  const [overridden, setOverridden] = useState(Boolean(record?.validation_overridden));
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [vendor, setVendor] = useState(record?.vendor || (record?.connect_order_report ? 'connect' : ''));
  const [orderId, setOrderId] = useState(record?.vendor_id || '');
  const [referenceId, setReferenceId] = useState(record?.vendor_reference || '');
  const [orderRecorded, setOrderRecorded] = useState(Boolean(record?.order_recorded));
  const [account, setAccount] = useState(
    (record?.vendor || '').trim().toLowerCase() === 'connect' ? (record?.vendor_specific_account || '').trim() : ''
  );
  const [isSaving, setIsSaving] = useState(false);
  const savingRef = useRef(false);

  const selectedAction = watch('action');

  const isUpdating = useMemo(() => !!record?.id, [record?.id]);

  const choices = useMemo(() => {
    if (!isUpdating) return checks;
    if (!record?.actions) return STATUS_CHOICES;
    const actions = new Set(record.actions);
    return STATUS_CHOICES.filter(({ id }) => actions.has(id as OneOffCheckRecord.Action));
  }, [checks, isUpdating, record?.actions]);

  const getSelectedAction = (actionId: number | string) => choices?.find((choice) => choice?.id === actionId);

  const proofRequired = useMemo(
    () =>
      isUpdating &&
      REQUIRES_PROOF?.includes(getSelectedAction(selectedAction)?.name as OneOffCheckRecord.Action) &&
      !fileUrl,
    [isUpdating, fileUrl, selectedAction]
  );

  const candidateName = [worker?.first_name, worker?.last_name].filter(Boolean).join(' ');
  const validationRecord = record ? { ...record, validation_overridden: overridden } : record;
  const validationBlocksSave = saveBlockedByValidation(validationRecord, fileUrl, validation.status);
  const canOverrideReport = Boolean(record?.can_override_report);

  const validateProof = (value: string, allValues: FormFields) => {
    if (!proofRequired) return undefined;
    return required()(value, allValues);
  };

  const onDrop = async (files: File[]) => {
    const file = files?.[0];
    if (!file?.name || uploadLock.current) return;

    uploadLock.current = true;
    dispatch(setIsLoading(true));
    setPendingFile({ name: file.name, size: file.size });
    setUploading(true);

    let uploaded = false;
    try {
      setValidation(IDLE_VALIDATION);
      const file_name = sanitizeFileName(`${_.snakeCase(record?.name)}_${record?.id}`);

      const s3Response: UploadToS3Response = await uploadToS3({
        acl: 'public-read',
        dir_name: import.meta.env.VITE_AWS_ONE_OFF_CHECKS_DIRNAME,
        doc_type: ValidDocTypes.custom_checks,
        file,
        file_name,
        turn_id: worker?.turn_id,
      });

      uploaded = true;
      setFileUrl(s3Response.location);
      setReportCleared(false);

      if (!requiresReportValidation(validationRecord)) return;

      const response = await runRequest({
        fetchType: 'UPDATE',
        resource: TurnResourcesEnum.FcraWorkers,
        params: {
          id: worker?.id,
          target: TurnTargetsEnum.OneOffChecks,
          targetId: record?.id,
          action: TurnActionsEnum.ValidateReport,
          data: { report_url: s3Response.location },
        },
        mode: 'silent',
      });

      setValidation(toValidationState(response?.data));
    } catch (error) {
      console.error(error);
      if (!uploaded) {
        setPendingFile(null);
        notify('Error: Failed to upload file', { type: 'error' });
        return;
      }
      setValidation(toValidationState(null));
    } finally {
      uploadLock.current = false;
      setUploading(false);
      dispatch(setIsLoading(false));
    }
  };

  const sendOverride = async (enabled: boolean, reason?: string) => {
    if (enabled && !reason?.trim()) return;

    const response = await runRequest({
      fetchType: 'UPDATE',
      resource: TurnResourcesEnum.FcraWorkers,
      params: {
        id: worker?.id,
        target: TurnTargetsEnum.OneOffChecks,
        targetId: record?.id,
        action: TurnActionsEnum.OverrideReportValidation,
        data: enabled ? { enabled: true, reason } : { enabled: false },
      },
      mode: 'silent',
    });

    if (response?.data?.validation_overridden !== enabled) {
      notify(OVERRIDE_ERROR_MESSAGE, { type: 'error' });
      return;
    }

    setOverridden(enabled);
    setShowOverrideModal(false);
  };

  const handleOverrideChange = (checked: boolean) => {
    if (!canOverrideReport) return;
    if (checked) {
      setShowOverrideModal(true);
      return;
    }
    void sendOverride(false);
  };

  const handleOverrideConfirm = (reason: string) => sendOverride(true, reason);

  const vendorValue = vendor.trim();
  const orderValue = orderId.trim();
  const referenceValue = referenceId.trim();
  const orderLocked = Boolean(record?.connect_order_report);
  const orderReady = Boolean(vendorValue && referenceValue);
  const storedAccount = (record?.vendor_specific_account || '').trim();
  const accountValue = vendorValue.toLowerCase() === 'connect' ? account.trim() : '';
  const accountTouched =
    isUpdating && !orderLocked && vendorValue.toLowerCase() === 'connect' && accountValue !== storedAccount;
  const orderTouched =
    isUpdating &&
    !orderLocked &&
    (vendorValue !== (record?.vendor || '').trim() ||
      orderValue !== (record?.vendor_id || '').trim() ||
      referenceValue !== (record?.vendor_reference || '').trim() ||
      accountTouched);
  const orderBlocked = orderTouched && !orderReady;
  const selectedName = getSelectedAction(selectedAction)?.name;
  const proofWithoutStatus = isUpdating && Boolean(fileUrl) && !selectedName;
  const willStatus = isUpdating && Boolean(selectedName) && !proofRequired;
  const willOrder = isUpdating && orderTouched && orderReady;
  const clearingReport = isUpdating && reportCleared && !fileUrl;
  const recordedTouched = isUpdating && orderRecorded !== Boolean(record?.order_recorded);
  const storedReportUrl = isUpdating && !reportCleared && !fileUrl ? record?.report_url || '' : '';
  const hasCheck = selectedAction !== undefined && selectedAction !== null && selectedAction !== '';
  const requestReady = !isUpdating && hasCheck && orderReady;
  const accountOptions = useMemo(() => {
    const names = (worker?.connect_account_names || []).map((name) => (name || '').trim()).filter(Boolean);
    const selected = accountValue;
    return [...new Set(selected ? [selected, ...names] : names)].sort((left, right) => left.localeCompare(right));
  }, [accountValue, worker?.connect_account_names]);
  const disabledReason = orderBlocked
    ? 'Enter a vendor and a Reference ID to save order changes.'
    : proofWithoutStatus
      ? 'Select a status to save the attached report.'
      : undefined;
  const orderPayload = {
    vendor: vendorValue,
    vendor_id: orderValue || null,
    vendor_reference: referenceValue || null,
    vendor_specific_account: vendorValue.toLowerCase() === 'connect' ? accountValue || null : null,
    order_recorded: orderRecorded,
  };
  const noteIdEdit = (nextOrder: string, nextReference: string) => {
    if (orderLocked) return;
    if (nextOrder.trim() || nextReference.trim()) setOrderRecorded(true);
  };
  const onVendorChange = (value: string) => {
    setVendor(value);
    if (value.trim().toLowerCase() !== 'connect') setAccount('');
  };
  const onOrderIdChange = (value: string) => {
    if (value !== orderId) noteIdEdit(value, referenceId);
    setOrderId(value);
  };
  const onReferenceIdChange = (value: string) => {
    if (value !== referenceId) noteIdEdit(orderId, value);
    setReferenceId(value);
  };

  const handleRequest = async (values: FormFields) => {
    if (savingRef.current || validationBlocksSave) return;

    const statusName = getSelectedAction(values.action)?.id;
    const sendingStatus = isUpdating && Boolean(statusName);
    const sendingOrder = isUpdating
      ? willOrder
      : orderReady && values.action !== undefined && values.action !== null && values.action !== '';

    if (proofWithoutStatus || (!sendingStatus && !sendingOrder && !clearingReport && !recordedTouched)) return;

    savingRef.current = true;
    setIsSaving(true);

    const notifyCopy = {
      onError: { message: `Error: One-Off Check was not ${isUpdating ? 'updated' : 'created'}` },
      onSuccess: {
        message: `One-Off Check ${isUpdating ? 'updated' : 'created'} successfully`,
        postAction: onClose,
      },
    };

    try {
      if (!isUpdating) {
        await runRequest({
          fetchType: 'CREATE',
          resource: TurnResourcesEnum.FcraWorkers,
          params: {
            id: worker?.id,
            target: TurnTargetsEnum.OneOffChecks,
            targetId: values.action,
            data: orderPayload,
          },
          mode: 'notify',
          notify: notifyCopy,
        });
        return;
      }

      const reportPayload = clearingReport
        ? { report_url: null, clear_report: true }
        : { report_url: fileUrl || record?.report_url || null };
      const statusData = { action: statusName, ...reportPayload };
      const recordedPayload = recordedTouched ? { order_recorded: orderRecorded } : {};
      const update =
        sendingStatus && sendingOrder
          ? { action: TurnActionsEnum.UpdateOneOffOrderAndStatus, data: { ...orderPayload, ...statusData } }
          : sendingOrder
            ? {
                action: TurnActionsEnum.UpdateOneOffOrder,
                data: { ...orderPayload, ...(clearingReport ? { clear_report: true } : {}) },
              }
            : sendingStatus
              ? { data: { ...statusData, ...recordedPayload } }
              : clearingReport
                ? { data: { clear_report: true, report_url: null, ...recordedPayload } }
                : { data: { order_recorded: orderRecorded } };

      await runRequest({
        fetchType: 'UPDATE',
        resource: TurnResourcesEnum.FcraWorkers,
        params: {
          id: worker?.id,
          target: TurnTargetsEnum.OneOffChecks,
          targetId: record?.id,
          ...update,
        },
        mode: 'notify',
        notify: notifyCopy,
      });
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  };

  return {
    canOverrideReport,
    checkName: record?.name,
    choices,
    disabled:
      isSaving ||
      (isUpdating
        ? proofRequired ||
          validationBlocksSave ||
          orderBlocked ||
          proofWithoutStatus ||
          (!willStatus && !willOrder && !clearingReport && !recordedTouched)
        : !requestReady),
    disabledReason,
    accountName: accountValue,
    accountOptions,
    onAccountChange: setAccount,
    onOrderIdChange,
    onOrderRecordedChange: setOrderRecorded,
    onReferenceIdChange,
    onVendorChange,
    orderId,
    orderLocked,
    orderRecorded,
    referenceId,
    requestedAt: record?.requested_at || '',
    requestedBy: record?.requested_by || '',
    vendor,
    fileInputOptions: { onDrop },
    fileUrl,
    onClearReport: () => setReportCleared(true),
    onRemovePendingFile: () => {
      setValue(path('file'), null as never);
      setPendingFile(null);
      setFileUrl(null);
      setReportCleared(false);
      setValidation(IDLE_VALIDATION);
    },
    pendingFile,
    reportCleared,
    storedReportUrl,
    uploading,
    handleOverrideChange,
    handleOverrideClose: () => setShowOverrideModal(false),
    handleOverrideConfirm,
    handleRequest: handleSubmit(handleRequest),
    isUpdating,
    overrideChecked: overridden,
    showOverride: isUpdating && Boolean(record?.requires_report_validation),
    showOverrideModal,
    onDrop,
    validateProof,
    validationFailures: validation.status === 'failed' ? validation.failures : [],
    validationRetry: validationRetryText(validation.status),
    validationSuccessLines:
      validation.status === 'passed'
        ? validationSuccessLines(validation.validated, candidateName, record?.one_off_check_type)
        : [],
  };
};

export default useController;
